import * as React from "react"
import { SelectSeeker, useTableSeeker } from "ui-seeker/core"
import { useTranslation } from "react-i18next"

// Default value normaliser: a scalar or an array of scalars → array of strings (empty values dropped).
export const toValueList = val => [].concat(val ?? []).filter(v => v !== null && v !== '').map(String)

// Nested fields ("price.site_name") for tables with enableNestedDataAccess: '.'.
const getField = (row, field) => field.split('.').reduce((obj, key) => obj?.[key], row)

const sortValues = (a, b) => a.localeCompare(b, undefined, { numeric: true })

// filterOptions.logic: keep the row when its value has any of the selected ones. Returns true to hide it.
export const selectFilterLogic = (toValues = toValueList) => (val, filters) => {
    if (!filters.length) return false
    const values = toValues(val)
    return !filters.some(f => values.includes(f))
}

// Custom mui-datatables filter: SelectSeeker styled like the native filters (.custom-filter-select, _table.scss),
// single or multi. Options come from the raw rows of the TableSeeker context (filterData holds React elements with
// customBodyRenderLite), so it works in any TableSeeker table without extra wiring.
// - toValues: row value → array of strings (default toValueList).
// - formatValue: value → text shown and searched (e.g. a translation); default the value itself.
// - renderOption: value → JSX inside the option (e.g. a logo); default formatValue.
// The option content goes in a div, never a span: ui-seeker hides every span inside a focused SelectSeeker
// (_select-seeker.scss, `:focus-within .selectSeeker span { display: none }`), which blanked the selected value.
export default function AppSelectFilter({ filterList, onChange, index, column, field, label, isMulti = false,
                                          toValues = toValueList, formatValue = v => v, renderOption = null }) {
    const { t } = useTranslation('common')
    const { tableData } = useTableSeeker()
    // isMulti: array of values; single: one value or null
    const [selected, setSelected] = React.useState(isMulti ? [...filterList[index]] : filterList[index][0] || null)

    const options = React.useMemo(() => {
        return [...new Set((tableData || []).flatMap(row => toValues(getField(row, field))))]
            .sort(sortValues)
            .map(v => ({ value: v, label: String(formatValue(v)) }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tableData, field])

    const handleChange = opt => {
        if (isMulti) {
            const vals = (opt || []).map(o => o.value)
            setSelected(vals)
            filterList[index] = vals
        } else {
            const val = opt ? opt.value : null
            setSelected(val)
            filterList[index] = val ? [val] : []
        }
        onChange(filterList[index], index, column)
    }

    return <div className="custom-filter-select" style={{ minWidth: '180px' }}>
        <span className="filter-select-label">{label}</span>
        <SelectSeeker
            classNamePrefix="selectSeeker"
            placeholder={t('all')}
            isMulti={isMulti}
            value={selected}
            options={options}
            formatOptionLabel={opt => <div className="filter-select-option">
                {renderOption ? renderOption(opt.value) : opt.label}
            </div>}
            isClearable
            // SelectSeeker shows a loader while options are empty; here the rows are already loaded, so a column with
            // no values must show "no options", not load forever.
            isLoading={false}
            onChange={handleChange}
        />
    </div>
}

// Column options for a custom select filter, to spread into a column's `options`:
//   options: { ...selectFilterOptions({ field: 'nights', label: i18n.t('report:columns.nights'), isMulti: true }), ... }
// field = the column's data key (dot path allowed); isMulti = several values at once (rows with any of them);
// toValues / formatValue / renderOption: see AppSelectFilter. With formatValue the filter chips above the table use it
// too: for custom filters mui-datatables passes the whole list of selected values to the chip render.
export const selectFilterOptions = ({ field, label, isMulti = false, toValues = toValueList, formatValue, renderOption }) => ({
    filter: true,
    filterType: 'custom',
    ...(formatValue && { customFilterListOptions: { render: v => [].concat(v ?? []).map(x => String(formatValue(x))) } }),
    filterOptions: {
        display: (filterList, onChange, index, column) =>
            <AppSelectFilter
                filterList={filterList}
                onChange={onChange}
                index={index}
                column={column}
                field={field}
                label={label}
                isMulti={isMulti}
                toValues={toValues}
                formatValue={formatValue}
                renderOption={renderOption}
            />,
        logic: selectFilterLogic(toValues)
    }
})
